import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { parkingZones } from '../../services/api';
import {
  Bloc, CarteLigne, Feuille, Fiche, Kv, LienOrdinateur, Liste, Pill, Plaque, Puces, Recherche, Segments, Statut,
  dateFr, depuis, messageErreur, useFicheOuverte, useToast,
} from './MobileUI';
import './ParkingMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Gestion Parking — version téléphone (2026-10-24), en CONSULTATION.
//
// Le cas d'usage : au parking, téléphone à la main, retrouver un véhicule et
// voir ce qui l'attend (OR, pièces, planning, facture, litige). La prise de
// vue reste dans la webapp d'inventaire (parking.autodom97.fr) ; ici, aucune
// écriture : statut, zone, responsable, notes, fusion se font sur ordinateur.
//
// Routes :
//   - liste : `GET /parking/mobile` (recherche serveur, 50 lignes au plus,
//     compteurs des files dans la même réponse). `GET /parking` complet pèse
//     ~2 Mo pour le parc entier : trop pour la 4G ;
//   - zones : `GET /parking-zones?concession_id=` (référentiel de la concession) ;
//   - fiche : `GET /parking/:id/photo` (à l'ouverture, si le relevé en a une)
//     et `GET /parking/:id/notes` (à l'ouverture du bloc Notes).
// Une plaque complète tapée qui donne un seul véhicule ouvre sa fiche.
// ═══════════════════════════════════════════════════════════════════════════

const SEUIL_NON_REVU = 30;
const LIMITE = 50;
const FILES = [
  { cle: 'tout', libelle: 'Tout' },
  { cle: 'a_identifier', libelle: 'À identifier', alerte: true },
  { cle: 'a_qualifier', libelle: 'À qualifier' },
  { cle: 'attendus', libelle: 'Attendus atelier' },
  { cle: 'pieces_recues', libelle: 'Pièces reçues' },
  { cle: 'factures', libelle: 'Facturés à livrer' },
  { cle: 'non_revus', libelle: `Non revus ${SEUIL_NON_REVU} j` },
  { cle: 'mes_vehicules', libelle: 'Mes véhicules' },
];
const NOTE = { info: { libelle: 'Info' }, a_faire: { libelle: 'À faire', ton: 'attente' }, urgent: { libelle: 'Urgent', ton: 'ko' } };

const normPlaque = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const joursDepuis = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);
const estIllisible = (v) => v.f_identifier && (!v.identifiant || v.identifiant.startsWith('NONLU-') || v.type_identifiant === 'INCONNU');
const nomResponsable = (v) => [v.responsable_prenom, v.responsable_nom].filter(Boolean).join(' ');
const aGps = (v) => v.latitude != null && v.longitude != null;
const lienMaps = (v) => `https://www.google.com/maps/search/?api=1&query=${v.latitude},${v.longitude}`;
const dateHeure = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};
const jour = (v) => (v ? String(v).split('T')[0] : '');
// Facture affichée seulement si aucun OR plus récent n'est ouvert (comme l'ordinateur).
const factureVisible = (v) => !!v.facture_date && !(v.or_date && jour(v.or_date) > jour(v.facture_date));

function pastillePieces(v) {
  if (!v.statut_commande) return null;
  const anomalie = v.nb_anomalies > 0 ? ' ⚠' : '';
  if (v.statut_commande === 'livre') return <Pill ton="ok">Pièces reçues{anomalie}</Pill>;
  if (v.statut_commande === 'recu_partiellement') return <Pill ton="attente">Pièces partielles{anomalie}</Pill>;
  return <Pill ton="attente">Pièces commandées{anomalie}</Pill>;
}
const libellePieces = (v) => ({ livre: 'Reçues', recu_partiellement: 'Reçues en partie', en_commande: 'Commandées' }[v.statut_commande] || '');

export default function ParkingMobile({ user, onVersionOrdinateur }) {
  const [recherche, setRecherche] = useState('');
  const [file, setFile] = useState('tout');
  const [zone, setZone] = useState('');
  // Concession : suit le choix du menu (2026-10-26) — la page est remontée à chaque changement.
  const concessionId = user?.concession_id ? String(user.concession_id) : '';
  const [donnees, setDonnees] = useState({ lignes: [], comptes: {}, total: 0, concessions: [], a_un_responsable: false });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [zones, setZones] = useState([]);
  const [feuille, setFeuille] = useState('');
  const [toast, afficher] = useToast();
  const sequence = useRef(0);
  const dejaOuvert = useRef('');

  const qNorm = normPlaque(recherche);

  // Chargement de la file : 300 ms après la dernière frappe ; une réponse
  // dépassée par une plus récente est ignorée.
  useEffect(() => {
    const n = ++sequence.current;
    setChargement(true);
    const minuteur = setTimeout(() => {
      const params = { file, limit: LIMITE };
      if (recherche.trim()) params.q = recherche.trim();
      if (zone) params.zone = zone;
      if (concessionId) params.concession_id = concessionId;
      api.get('/parking/mobile', { params })
        .then((r) => {
          if (n !== sequence.current) return;
          setDonnees(r.data); setErreur('');
        })
        .catch((e) => { if (n === sequence.current) setErreur(messageErreur(e, 'Le parking n’a pas pu être chargé.')); })
        .finally(() => { if (n === sequence.current) setChargement(false); });
    }, recherche ? 300 : 0);
    return () => clearTimeout(minuteur);
  }, [recherche, file, zone, concessionId]);

  // Zones de la concession (référentiel parking_zones).
  useEffect(() => {
    if (!concessionId) { setZones([]); return undefined; }
    let actif = true;
    parkingZones.listNoms(concessionId)
      .then((r) => { if (actif) setZones((r.data.zones || []).map((z) => z.nom)); })
      .catch(() => { if (actif) setZones([]); });
    return () => { actif = false; };
  }, [concessionId]);

  const lignes = useMemo(() => donnees.lignes || [], [donnees.lignes]);
  const cle = useCallback((v) => v.id, []);
  const fiche = useFicheOuverte(lignes, cle);
  const { ouvrir } = fiche;

  // Plaque complète, un seul véhicule : on ouvre sa fiche (une fois par saisie).
  useEffect(() => {
    if (chargement || qNorm.length < 5 || lignes.length !== 1) return;
    if (normPlaque(lignes[0].identifiant) !== qNorm || dejaOuvert.current === qNorm) return;
    dejaOuvert.current = qNorm;
    ouvrir(0);
  }, [chargement, lignes, qNorm, ouvrir]);

  const comptes = donnees.comptes || {};
  const puces = FILES
    .filter((f) => f.cle !== 'mes_vehicules' || donnees.a_un_responsable)
    .map((f) => ({ ...f, compte: comptes[f.cle] ?? null }));
  const plusieursConcessions = (donnees.concessions || []).length > 1;

  const changerRecherche = (v) => setRecherche(String(v || '').toUpperCase().replace(/-/g, ''));
  const choisirZone = (z) => { setZone(z); setFeuille(''); };

  const optionsZone = [{ cle: '', libelle: 'Toutes' }, ...zones.map((z) => ({ cle: z, libelle: z }))];
  const reste = (donnees.total || 0) - lignes.length;

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-pk-q" valeur={recherche} onChange={changerRecherche} placeholder="Plaque, VIN (sans tirets), client" />
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        {zones.length > 3 && (
          <div className="mb-rangee">
            <button type="button" className={`mb-mini${zone ? ' mb-mini--accent' : ''}`} onClick={() => setFeuille('zone')}>
              Zone : {zone || 'Toutes'} ▾
            </button>
          </div>
        )}
        {zones.length > 0 && zones.length <= 3 && (
          <Segments label="Zone" valeur={zone} onChange={setZone} options={optionsZone} />
        )}
      </div>
      <Puces label="Files" valeur={file} onChange={setFile} options={puces} />

      <Liste
        elements={lignes}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={recherche ? 'Aucun véhicule du parc ne correspond.' : 'Aucun véhicule dans cette file.'}
        onOuvrir={fiche.ouvrir}
        carte={(v) => {
          const j = joursDepuis(v.vu_le);
          return {
            couleur: v.statut_couleur,
            contenu: (
              <>
                <CarteLigne
                  avant={estIllisible(v) ? <span className="mb-pk-illisible">illisible</span> : <Plaque immat={v.identifiant} />}
                  titre={v.marque || 'Marque inconnue'}
                  sous={[v.allee || 'Sans zone', plusieursConcessions && !concessionId ? v.concession : null].filter(Boolean).join(' · ')}
                  age={depuis(v.vu_le)}
                  retard={j > SEUIL_NON_REVU}
                />
                <span className="mb-carte-meta">
                  <Statut libelle={v.statut_nom || v.statut || '—'} couleur={v.statut_couleur} />
                  {v.num_or && <Pill ton="accent">OR {v.num_or}</Pill>}
                  {pastillePieces(v)}
                  {factureVisible(v) && <Pill ton="ok">Facturé {dateFr(jour(v.facture_date))}</Pill>}
                  {v.litige_id && <Pill ton="ko">Litige</Pill>}
                  {v.notes_count > 0 && <Pill>💬 {v.notes_count}</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      {!erreur && reste > 0 && (
        <p className="mb-aide mb-aide--centre">
          {lignes.length} véhicules affichés sur {donnees.total} : précisez la recherche ou choisissez une zone.
        </p>
      )}
      <LienOrdinateur onClick={onVersionOrdinateur}>Statut, zone, notes, rapport, carte : version ordinateur</LienOrdinateur>

      {feuille === 'zone' && (
        <Feuille titre="Zone" onFermer={() => setFeuille('')}>
          <div className="mb-options">
            {optionsZone.map((o) => (
              <button key={o.cle || 'toutes'} type="button" className={`mb-option${zone === o.cle ? ' is-actif' : ''}`} onClick={() => choisirZone(o.cle)}>
                {o.libelle}
              </button>
            ))}
          </div>
        </Feuille>
      )}

      {fiche.ouverte && (
        <FicheVehicule
          key={fiche.ouverte.id}
          v={fiche.ouverte}
          position={fiche.index}
          total={lignes.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FicheVehicule({ v, position, total, onRetour, onPrecedent, onSuivant, afficher, onVersionOrdinateur }) {
  const [photo, setPhoto] = useState(null);
  const [photoEtat, setPhotoEtat] = useState(v.a_photo ? 'chargement' : 'aucune');
  const [agrandie, setAgrandie] = useState(false);
  const [notes, setNotes] = useState(null);
  const [notesEtat, setNotesEtat] = useState('');

  // Photo du dernier relevé, chargée à l'ouverture de la fiche.
  useEffect(() => {
    if (!v.a_photo) return undefined;
    let actif = true;
    api.get(`/parking/${v.id}/photo`)
      .then((r) => {
        if (!actif) return;
        if (r.data.image_data) { setPhoto(r.data.image_data); setPhotoEtat(''); } else setPhotoEtat('aucune');
      })
      .catch((e) => { if (actif) setPhotoEtat(messageErreur(e, 'Photo indisponible.')); });
    return () => { actif = false; };
  }, [v.id, v.a_photo]);

  const chargerNotes = () => {
    if (notes !== null || notesEtat === 'chargement') return;
    setNotesEtat('chargement');
    api.get(`/parking/${v.id}/notes`)
      .then((r) => { setNotes(r.data.notes || []); setNotesEtat(''); })
      .catch((e) => setNotesEtat(messageErreur(e, 'Notes indisponibles.')));
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(v.identifiant || '');
      afficher('Identifiant copié');
    } catch (e) {
      afficher('Copie impossible sur cet appareil');
    }
  };

  const illisible = estIllisible(v);
  const j = joursDepuis(v.vu_le);
  const responsable = nomResponsable(v);
  const avenir = v.date_planification && v.date_planification >= new Date().toISOString().slice(0, 10);
  const attentes = [
    v.num_or && ['OR en cours', `n° ${v.num_or}${v.or_date ? ` · ouvert le ${dateFr(jour(v.or_date))}` : ''}`],
    v.statut_commande && ['Pièces', (
      <span className="mb-pk-valeur">
        {libellePieces(v)}{v.nb_lignes_commande ? ` · ${v.nb_lignes_commande} ligne${v.nb_lignes_commande > 1 ? 's' : ''}` : ''}
        {v.nb_anomalies > 0 && <Pill ton="ko">⚠ {v.nb_anomalies} anomalie{v.nb_anomalies > 1 ? 's' : ''}</Pill>}
      </span>
    )],
    // Pastille de couleur (teintes pastel du Planning, illisibles en texte sur blanc).
    v.planning_statut_nom && ['Statut atelier', (
      <Statut libelle={v.planning_statut_nom} couleur={v.planning_statut_couleur} />
    )],
    v.date_planification && [avenir ? 'Planifié le' : 'Dernière planification', dateFr(v.date_planification)],
    factureVisible(v) && ['Dernière facture', dateFr(jour(v.facture_date))],
    v.litige_id && ['Litige', <Pill ton="ko">En cours · n° {v.litige_id}</Pill>],
  ].filter(Boolean);

  return (
    <Fiche
      titre={illisible ? 'Plaque illisible' : v.identifiant}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={(
        <>
          <button type="button" className="mb-bouton" onClick={copier} disabled={!v.identifiant}>Copier</button>
          {aGps(v)
            ? <a className="mb-bouton mb-bouton--principal mb-pk-lien" href={lienMaps(v)} target="_blank" rel="noopener noreferrer">Ouvrir dans Maps</a>
            : <button type="button" className="mb-bouton mb-bouton--principal" disabled>Pas de position GPS</button>}
        </>
      )}
    >
      <div className="mb-entete">
        <div className="mb-entete-haut">
          {illisible ? <span className="mb-pk-illisible">illisible</span> : <Plaque immat={v.identifiant} />}
          {v.type_identifiant && v.type_identifiant !== 'PLAQUE' && <span>{v.type_identifiant}</span>}
        </div>
        <div>
          <div className="mb-entete-titre">{v.marque || 'Marque inconnue'}</div>
          <div className="mb-entete-sous">{[v.allee || 'Sans zone', v.concession].filter(Boolean).join(' · ')}</div>
        </div>
      </div>

      <div className="mb-pk-photo">
        {photo && (
          <button type="button" onClick={() => setAgrandie(true)} aria-label="Agrandir la photo du relevé">
            <img src={`data:image/jpeg;base64,${photo}`} alt="" />
          </button>
        )}
        {!photo && (
          <p className={`mb-aide mb-aide--centre${photoEtat && photoEtat !== 'chargement' && photoEtat !== 'aucune' ? ' mb-aide--erreur' : ''}`}>
            {photoEtat === 'chargement' ? 'Chargement de la photo…' : photoEtat === 'aucune' ? 'Pas de photo pour ce relevé.' : photoEtat}
          </p>
        )}
        <small>Photo du dernier relevé · {depuis(v.vu_le)}</small>
      </div>

      <div className="mb-statut-bouton mb-pk-statut" style={{ '--mb-c': v.statut_couleur || 'var(--border-strong)' }}>
        <span className="mb-statut-pastille" />
        {/* Libellé en couleur de texte normale : plusieurs teintes du référentiel
            (gris clair de « Nouveau ») sont illisibles sur fond blanc ; la
            pastille porte la couleur. */}
        <b>{v.statut_nom || v.statut || '—'}</b>
        <small>{responsable ? `Responsable : ${responsable}` : 'Aucun responsable'}</small>
      </div>

      <Bloc titre="Où il est" resume={v.allee || 'sans zone'} ouvert>
        <Kv lignes={[
          ['Zone', v.allee],
          ['Concession', v.concession],
          ['Vu le', (
            <span className={j > SEUIL_NON_REVU ? 'mb-pk-retard' : undefined}>
              {dateHeure(v.vu_le)}{v.agent ? ` · ${v.agent}` : ''}
            </span>
          )],
          ['1re détection', dateFr(v.created_at)],
        ]} />
        {aGps(v)
          ? (
            <a className="mb-carton mb-pk-maps" href={lienMaps(v)} target="_blank" rel="noopener noreferrer">
              <span>📍 Position GPS du relevé</span><b>Ouvrir dans Maps</b>
            </a>
          )
          : <p className="mb-aide">Ce relevé n’a pas de position GPS.</p>}
      </Bloc>

      <Bloc titre="Ce qui l’attend" resume={attentes.length ? attentes.map((a) => a[0]).join(' · ') : 'rien de connu'} ouvert={attentes.length > 0}>
        {attentes.length
          ? <Kv lignes={attentes} />
          : <p className="mb-aide">Aucun OR, pièce, planning, facture ni litige rapproché de cet identifiant.</p>}
      </Bloc>

      <Bloc titre="Notes" compte={v.notes_count || null} resume={v.notes_count ? '' : 'aucune'} onOuvrir={chargerNotes}>
        {notesEtat === 'chargement' && <p className="mb-aide">Chargement des notes…</p>}
        {notesEtat && notesEtat !== 'chargement' && <p className="mb-aide mb-aide--erreur">{notesEtat}</p>}
        {notes && notes.length === 0 && <p className="mb-aide">Aucune note.</p>}
        {notes && notes.map((n) => {
          const s = NOTE[n.statut] || NOTE.info;
          return (
            <div key={n.id} className="mb-commentaire">
              {n.statut && n.statut !== 'info' && <><Pill ton={s.ton}>{s.libelle}</Pill>{' '}</>}
              {n.contenu}
              <small>{[[n.prenom, n.nom].filter(Boolean).join(' '), dateHeure(n.created_at)].filter(Boolean).join(' · ')}</small>
            </div>
          );
        })}
      </Bloc>

      <p className="mb-aide mb-aide--centre">Statut, zone, responsable, notes, correction de plaque et fusion : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {agrandie && photo && (
        <div className="mb-pk-plein" role="dialog" aria-modal="true" aria-label="Photo du relevé">
          <div className="mb-pk-plein-barre">
            <span>{illisible ? 'Plaque illisible' : v.identifiant}</span>
            <button type="button" onClick={() => setAgrandie(false)}>Fermer</button>
          </div>
          <img src={`data:image/jpeg;base64,${photo}`} alt={`Relevé de ${illisible ? 'la plaque illisible' : v.identifiant}`} />
        </div>
      )}
    </Fiche>
  );
}
